import { useEffect, useRef, useState } from 'react'
import { createFileRoute, redirect, useNavigate, useBlocker } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { hasTokens } from '../lib/auth'
import {
  fetchScdRequest,
  fetchScdAdjacent,
  fetchAppointment,
  moveScdRequest,
  setScdScadenza,
  setScdStatus,
  updateScdFields,
  type ScdRequest,
} from '../lib/api'
import { ProfessionalPicker } from '../components/ProfessionalPicker'
import { useMe } from '../hooks/useAuth'
import { ScdChecklistTab } from '../components/scd/ScdChecklistTab'
import { ScdEventTimeline } from '../components/scd/ScdEventTimeline'
import { ScdPipeline } from '../components/scd/ScdPipeline'
import { ScdCommentThread } from '../components/scd/ScdCommentThread'
import { useSavableField } from '../hooks/useSavableField'
import { ScdFissataModal } from '../components/scd/ScdFissataModal'
import { UnsavedChangesModal } from '../components/UnsavedChangesModal'
import { STAGE_LABELS, NEXT_STAGES } from '../components/scd/ScdBoardCard'
import { useToast } from '../components/Toast'
import { CardSkeleton } from '../components/Skeleton'
import { MutuabileBadge } from '../components/MutuabileBadge'

export const Route = createFileRoute('/scd-board/$id')({
  validateSearch: (s: Record<string, unknown>): { openFissata?: boolean } => ({
    openFissata: typeof s.openFissata === 'boolean' ? s.openFissata : undefined,
  }),
  beforeLoad: () => {
    if (!hasTokens()) throw redirect({ to: '/login' })
  },
  component: ScdDetailPage,
})

const ZONE_B = new Set(['domanda_asta', 'data_vendita', 'non_aggiudicata', 'aggiudicata'])
const _GIULIA = 'scarafoni.g.agl@gmail.com'

const TIPOLOGIA_ACQ: Record<string, string> = {
  prima_casa: 'Prima casa',
  seconda_casa: 'Seconda casa',
  societa: 'Società',
}

function fmtMoney(v: number | null | undefined) {
  if (v == null) return '—'
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
}

function daysTo(iso: string | null | undefined): number | null {
  if (!iso) return null
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
}

function toLocalDatetimeInputValue(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  const offsetMs = d.getTimezoneOffset() * 60_000
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16)
}

function DaysBadge({ days }: { days: number | null }) {
  if (days == null) return null
  const color = days <= 10 ? 'risk' : days <= 20 ? 'warn' : 'ok'
  const label = days < 0 ? 'scaduta' : days === 0 ? 'scade oggi' : `tra ${days}g`
  return <span className={`chip ${color} sm`} style={{ fontFamily: 'var(--font-mono)', marginLeft: 6 }}>{label}</span>
}

// ===== Sub-components =====


function StageMover({ req, canZoneB, onMoved, onRequestFissata }: { req: ScdRequest; canZoneB: boolean; onMoved: (u: ScdRequest) => void; onRequestFissata: () => void }) {
  const toast = useToast()
  const [moving, setMoving] = useState<string | null>(null)
  const [showMotiv, setShowMotiv] = useState(false)
  const [motivazione, setMotivazione] = useState('')
  const [pendingStage, setPendingStage] = useState<string | null>(null)

  const nextStages = (NEXT_STAGES[req.stage] ?? []).filter(s => {
    if (ZONE_B.has(s) && !canZoneB) return false
    return true
  })

  const doMove = async (toStage: string, extra?: { motivazione?: string; data_presentazione?: string }) => {
    setMoving(toStage)
    try {
      const updated = await moveScdRequest(req.id, {
        to_stage: toStage,
        version: req.version,
        ...(extra?.motivazione ? { motivazione: extra.motivazione } : {}),
        ...(extra?.data_presentazione ? { data_presentazione: new Date(extra.data_presentazione).toISOString() } : {}),
      })
      onMoved(updated)
      toast.success(`→ ${STAGE_LABELS[toStage] ?? toStage}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Errore avanzamento')
    } finally {
      setMoving(null)
      setShowMotiv(false)
      setMotivazione('')
      setPendingStage(null)
    }
  }

  if (nextStages.length === 0) {
    return <p className="muted" style={{ fontSize: 'var(--fs-13)', margin: 0 }}>Nessun avanzamento disponibile.</p>
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', alignItems: 'center' }}>
      {!canZoneB && ZONE_B.has(req.stage) && (
        <p className="muted" style={{ fontSize: 'var(--fs-12)', margin: 0, width: '100%' }}>Zone B — solo supervisore o Giulia.</p>
      )}
      {nextStages.map(s => {
        const needsMotivazione = s === 'esito_neg_scd' || s === 'annullata'
        const isFissate = s === 'scd_fissate'
        return (
          <button
            key={s}
            type="button"
            className={`btn sm ${needsMotivazione ? 'risk' : ''}`}
            disabled={moving !== null}
            onClick={() => {
              if (needsMotivazione) { setPendingStage(s); setShowMotiv(true) }
              else if (isFissate) { onRequestFissata() }
              else void doMove(s)
            }}
          >
            {moving === s ? '…' : `→ ${STAGE_LABELS[s] ?? s}`}
          </button>
        )
      })}

      {showMotiv && (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
          <textarea
            rows={2}
            value={motivazione}
            onChange={e => setMotivazione(e.target.value)}
            placeholder={pendingStage === 'annullata' ? 'Motivazione annullamento…' : 'Motivazione esito negativo…'}
            style={{ fontFamily: 'inherit', fontSize: 'var(--fs-13)', padding: 'var(--sp-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}
          />
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <button type="button" className="btn sm risk" disabled={!motivazione.trim() || moving !== null} onClick={() => void doMove(pendingStage!, { motivazione })}>
              {moving ? '…' : (pendingStage === 'annullata' ? 'Conferma annullamento' : 'Conferma esito negativo')}
            </button>
            <button type="button" className="btn sm" onClick={() => { setShowMotiv(false); setPendingStage(null) }}>Annulla</button>
          </div>
        </div>
      )}
    </div>
  )
}

function FlagRow({ label, on, onChange, cls }: { label: string; on: boolean; onChange: (v: boolean) => void; cls: string }) {
  return (
    <div className={`flag-row${on ? ` ${cls}` : ''}`} onClick={() => onChange(!on)}>
      <span className="fdot" />
      <span>{label}</span>
      <span style={{ marginLeft: 'auto', fontSize: 'var(--fs-11)', opacity: on ? 1 : 0.5 }}>{on ? 'Attivo' : 'Off'}</span>
    </div>
  )
}

function NoteEditor({ req, onUpdate, onDirtyChange }: { req: ScdRequest; onUpdate: (u: ScdRequest) => void; onDirtyChange?: (dirty: boolean) => void }) {
  const toast = useToast()
  const { value: val, setValue: setVal, dirty, saving, doSave } = useSavableField(
    req.note ?? '',
    (v) => updateScdFields(req.id, { note: v, version: req.version }),
    { onSaved: (u) => { onUpdate(u); toast.success('Nota salvata') }, onError: () => toast.error('Errore') },
  )
  useEffect(() => { onDirtyChange?.(dirty) }, [dirty, onDirtyChange])
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <textarea
        rows={3}
        value={val}
        onChange={e => setVal(e.target.value)}
        placeholder="Note interne…"
        style={{ fontFamily: 'inherit', fontSize: 'var(--fs-13)', padding: 'var(--sp-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <button type="button" className={`btn sm${dirty ? ' dirty' : ''}`} disabled={saving} onClick={() => void doSave()}>
          {saving ? '…' : 'Salva'}
        </button>
        {dirty && <span className="dirty-badge">da salvare</span>}
      </div>
    </div>
  )
}

function LorenzoStatus({ req }: { req: ScdRequest }) {
  const inviato = req.lorenzo_inviato ?? false
  return (
    <div className="lorenzo-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        Inviato a Lorenzo (broker)
        {inviato ? <span className="chip ok sm">Sì</span> : <span className="chip sm">No</span>}
      </div>

      {inviato && (
        <>
          <MutuabileBadge lorenzoInviato={inviato} mutuabile={req.mutuabile ?? null} />
          {req.lorenzo_esito && (
            <div className="muted" style={{ fontSize: 'var(--fs-12)', marginTop: 'var(--sp-2)' }}>
              {req.lorenzo_esito}
            </div>
          )}
        </>
      )}

      <a
        href={`/lotti/${req.property_id}`}
        className="muted"
        style={{ fontSize: 'var(--fs-11)', display: 'block', marginTop: 'var(--sp-2)' }}
      >
        Modifica dal dettaglio lotto →
      </a>
    </div>
  )
}

// ===== Main page =====

function ScdDetailPage() {
  const { id } = Route.useParams()
  const qc = useQueryClient()
  const { data: me } = useMe()
  const toast = useToast()

  const { data: req, isLoading, error } = useQuery({
    queryKey: ['scd-request', id],
    queryFn: () => fetchScdRequest(id),
  })

  const { data: adjacent } = useQuery({
    queryKey: ['scd-adjacent', id],
    queryFn: () => fetchScdAdjacent(id),
    staleTime: 60_000,
  })

  const { data: presentationAppt } = useQuery({
    queryKey: ['appointment', req?.presentation_appointment_id],
    queryFn: () => fetchAppointment(req!.presentation_appointment_id!),
    enabled: !!req?.presentation_appointment_id,
    staleTime: 300_000,
  })

  // Modale fissaggio SCD — apribile direttamente dal kebab di questa pagina
  // (onRequestFissata sotto), oppure auto-aperto arrivando da kanban kebab/DnD
  // via navigate a ?openFissata=true (ScdBoardCard.tsx / ScdBoard.tsx). Stato
  // iniziale letto dal search param (lazy initializer, gira al mount — questa
  // pagina rimonta sempre arrivando da un altro punto, il pager adiacente usa
  // window.location.href) cosi' l'effetto sotto fa solo l'effetto collaterale
  // vero (ripulire l'URL, per non riaprire il modale a un refresh) senza
  // chiamare setState nel suo corpo.
  const navigate = useNavigate({ from: '/scd-board/$id' })
  const search = Route.useSearch()
  const [fissataModalOpen, setFissataModalOpen] = useState(() => !!search.openFissata)

  useEffect(() => {
    if (search.openFissata) {
      void navigate({ search: { ...search, openFissata: undefined }, replace: true })
    }
  }, [search, navigate])

  const handleUpdate = (_updated: ScdRequest) => {
    void qc.invalidateQueries({ queryKey: ['scd-request', id] })
    void qc.invalidateQueries({ queryKey: ['scd-requests-list'] })
    void qc.invalidateQueries({ queryKey: ['scd-board'] })
  }

  const [noteDirty, setNoteDirty] = useState(false)
  const [scadenzaDirty, setScadenzaDirty] = useState(false)
  const [statusDirty, setStatusDirty] = useState(false)
  const [contattoDirty, setContattoDirty] = useState(false)
  const [checklistDirty, setChecklistDirty] = useState(false)
  const anyDirty = noteDirty || scadenzaDirty || statusDirty || contattoDirty || checklistDirty

  const blocker = useBlocker({
    shouldBlockFn: () => anyDirty,
    enableBeforeUnload: () => anyDirty,
    withResolver: true,
  })

  // flag toggles
  const toggleFlag = async (field: 'consulente_sollecitato' | 'contattare_delegato', val: boolean) => {
    if (!req) return
    try {
      const updated = await updateScdFields(req.id, { [field]: val, version: req.version })
      handleUpdate(updated)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      toast.error(`Errore flag: ${msg}`)
    }
  }

  // anchor nav
  const sectionRefs = {
    workflow: useRef<HTMLElement>(null),
    'cliente-lotto': useRef<HTMLElement>(null),
    'dati-scheda': useRef<HTMLElement>(null),
    redazione: useRef<HTMLElement>(null),
    professionisti: useRef<HTMLElement>(null),
    appuntamento: useRef<HTMLElement>(null),
    documenti: useRef<HTMLElement>(null),
    'domanda-esito': useRef<HTMLElement>(null),
  }
  const [activeAnchor, setActiveAnchor] = useState('workflow')

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          if (e.isIntersecting) setActiveAnchor(e.target.id)
        }
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    )
    Object.values(sectionRefs).forEach(r => { if (r.current) obs.observe(r.current) })
    return () => obs.disconnect()
  }, [!!req])

  if (isLoading) return <CardSkeleton />
  if (error || !req) return <div className="chip risk">Errore caricamento SCD</div>

  const canZoneB = me?.role === 'supervisore' || me?.email === _GIULIA
  const clientName = [req.client_cognome, req.client_nome].filter(Boolean).join(' ') || '—'
  const propCode = req.property_short_code ? `L_${String(req.property_short_code).padStart(5, '0')}` : '—'
  const scdCode = `R_${String(req.short_code).padStart(5, '0')}`

  const days = daysTo(req.property_data_asta)
  const priorityColor = days == null ? null : days <= 10 ? 'var(--risk)' : days <= 20 ? 'var(--warn)' : 'var(--ok)'
  const priorityBg = days == null ? null : days <= 10 ? 'var(--risk-soft)' : days <= 20 ? 'var(--warn-soft)' : 'var(--ok-soft)'
  const priorityInk = days == null ? null : days <= 10 ? 'var(--risk-ink)' : days <= 20 ? 'var(--warn-ink)' : 'var(--ok-ink)'
  const priorityLine = days == null ? null : days <= 10 ? 'var(--risk-line)' : days <= 20 ? 'var(--warn-line)' : 'var(--ok-line)'

  const stageCss = req.stage.replace(/_/g, '-').replace('scd-pronta-fissare', 'scd-pronta').replace('esito-pos-checklist', 'esito-pos').replace('esito-neg-scd', 'esito-neg')
  const scdStatusMap: Record<string, { label: string; cls: string }> = {
    scheda_da_fare: { label: 'Scheda da fare', cls: 'risk' },
    scheda_pronta:  { label: 'Scheda pronta', cls: 'ok' },
    scd_da_fix:     { label: 'SCD da fix', cls: 'warn' },
    scheda_presentata: { label: 'Presentata', cls: 'ok' },
  }
  const scdSt = req.scd_status ? scdStatusMap[req.scd_status] : null

  const ANCHORS = [
    { id: 'workflow', label: 'Workflow' },
    { id: 'cliente-lotto', label: 'Cliente & Lotto' },
    { id: 'dati-scheda', label: 'Dati Scheda' },
    { id: 'redazione', label: 'Redazione' },
    { id: 'professionisti', label: 'Professionisti' },
    { id: 'appuntamento', label: 'Appuntamento' },
    { id: 'documenti', label: 'Documenti' },
    { id: 'domanda-esito', label: 'Domanda / Esito' },
  ]

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* === Crumb row === */}
      <div className="crumb-row" style={{ position: 'sticky', top: 0, zIndex: 15 }}>
        <nav className="breadcrumb">
          <a href="/scd-board" style={{ color: 'var(--ink-3)' }}>← Schede SCD</a>
          <span className="sep">/</span>
          <span className="here">{scdCode}</span>
          <span className="sep">·</span>
          <span style={{ color: 'var(--ink-2)' }}>{clientName}</span>
        </nav>
        {/* pager */}
        {adjacent && (
          <div className="lot-pager">
            <button
              type="button"
              disabled={!adjacent.prev_id}
              onClick={() => adjacent.prev_id && (window.location.href = `/scd-board/${adjacent.prev_id}`)}
              title="Precedente"
            >‹</button>
            {adjacent.position != null && (
              <span className="ix">{adjacent.position} di {adjacent.total}</span>
            )}
            <button
              type="button"
              disabled={!adjacent.next_id}
              onClick={() => adjacent.next_id && (window.location.href = `/scd-board/${adjacent.next_id}`)}
              title="Successivo"
            >›</button>
          </div>
        )}
      </div>

      {/* === Hero === */}
      <div style={{ padding: 'var(--sp-5) var(--sp-6)', background: 'var(--surface)', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--sp-4)' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-13)', fontWeight: 600, background: 'var(--ink-1)', color: '#fff', padding: '2px 8px', borderRadius: 'var(--r-sm)', letterSpacing: '0.01em' }}>
              {scdCode}
            </span>
          </div>
          <h1 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-20)', fontWeight: 600, letterSpacing: '-0.012em', color: 'var(--ink-1)' }}>
            {clientName}{req.property_data_asta ? ` — Asta ${fmtDate(req.property_data_asta)}` : ''}
          </h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: 'var(--fs-12)', color: 'var(--ink-3)', alignItems: 'center' }}>
            {req.client_id && (
              <a href={`/clienti/${req.client_id}`} style={{ color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>C_{String(req.client_id).slice(-5).padStart(5,'0')}</a>
            )}
            {req.client_id && req.property_id && <span>·</span>}
            {req.property_id && (
              <a href={`/lotti/${req.property_id}`} style={{ color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{propCode}</a>
            )}
            {req.property_rge && <><span>·</span><span>{req.property_rge}</span></>}
          </div>
        </div>
        {/* pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', alignItems: 'center', flexShrink: 0 }}>
          {days != null && priorityColor && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, height: 26, padding: '0 10px', borderRadius: 'var(--r-pill)', background: priorityBg!, color: priorityInk!, border: `1px solid ${priorityLine!}`, fontSize: 'var(--fs-12)', fontWeight: 500 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: priorityColor }} />
              {Math.abs(days)} {days < 0 ? 'giorni fa' : 'giorni all\'asta'}
            </span>
          )}
          <span className={`stage ${stageCss} lg`}>{STAGE_LABELS[req.stage] ?? req.stage}</span>
          {scdSt && <span className={`chip ${scdSt.cls}`}>{scdSt.label}</span>}
          {req.tipo_vendita && (
            <span className={`tag tab-${req.tipo_vendita === 'telematica' ? 'telematica' : 'fisica'}`}>
              {req.tipo_vendita === 'telematica' ? 'Telematica' : 'Fisica'}
            </span>
          )}
        </div>
      </div>

      {/* === Pipeline === */}
      <ScdPipeline stage={req.stage} lastAdvancedAt={req.last_advanced_at} lastAdvancedBy={req.last_advanced_by} />

      {/* === Anchor nav === */}
      <nav className="anchor-nav">
        {ANCHORS.map(a => (
          <a
            key={a.id}
            href={`#${a.id}`}
            className={activeAnchor === a.id ? 'on' : ''}
            onClick={e => {
              e.preventDefault()
              sectionRefs[a.id as keyof typeof sectionRefs]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
          >
            {a.label}
          </a>
        ))}
        {req.last_advanced_at && (
          <div className="nav-end">
            Ultima modifica: {new Date(req.updated_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} · {req.owner_name ?? '—'}
          </div>
        )}
      </nav>

      {/* === Body grid === */}
      <div className="detail-grid">
        {/* === Main === */}
        <div className="detail-main">

          {/* Workflow */}
          <section id="workflow" ref={sectionRefs.workflow} className="section">
            <div className="section-head">
              <div className="title">Workflow · Avanza fase</div>
            </div>
            <div className="section-body">
              {/* action bar */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', paddingBottom: 'var(--sp-3)', borderBottom: '1px solid var(--line)', marginBottom: 'var(--sp-3)' }}>
                <button
                  type="button"
                  className={`btn sm ${req.consulente_sollecitato ? 'warn' : ''}`}
                  onClick={() => void toggleFlag('consulente_sollecitato', !req.consulente_sollecitato)}
                >
                  {req.consulente_sollecitato ? '⚑ Sollecitato' : 'Sollecita consulente'}
                </button>
                <button
                  type="button"
                  className={`btn sm ${req.contattare_delegato ? 'accent' : ''}`}
                  onClick={() => void toggleFlag('contattare_delegato', !req.contattare_delegato)}
                >
                  {req.contattare_delegato ? '● Da contattare' : 'Contatta delegato'}
                </button>
                <button type="button" className="btn sm" onClick={() => sectionRefs.redazione.current?.scrollIntoView({ behavior: 'smooth' })}>
                  Imposta scadenza redazione
                </button>
                <button type="button" className="btn sm" onClick={() => sectionRefs.appuntamento.current?.scrollIntoView({ behavior: 'smooth' })}>
                  Fissa presentazione
                </button>
              </div>
              <StageMover req={req} canZoneB={canZoneB} onMoved={handleUpdate} onRequestFissata={() => setFissataModalOpen(true)} />
              {req.motivazione && (
                <div style={{ marginTop: 'var(--sp-3)', padding: 'var(--sp-3)', background: 'var(--risk-soft)', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-13)', color: 'var(--risk-ink)' }}>
                  <strong>Motivazione esito:</strong> {req.motivazione}
                </div>
              )}
            </div>
          </section>

          {/* Cliente & Lotto */}
          <section id="cliente-lotto" ref={sectionRefs['cliente-lotto']} className="section">
            <div className="section-head">
              <div className="title">Cliente & Lotto</div>
              <div className="actions">
                {req.client_id && <a href={`/clienti/${req.client_id}`} className="btn sm ghost">↗ Cliente</a>}
                {req.property_id && <a href={`/lotti/${req.property_id}`} className="btn sm ghost">↗ {propCode}</a>}
              </div>
            </div>
            <div className="section-body tight">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: 0 }}>
                {/* cliente */}
                <div style={{ padding: 'var(--sp-3) var(--sp-4)', borderRight: '1px solid var(--line)' }}>
                  <div style={{ fontSize: 'var(--fs-10)', color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--sp-2)', fontWeight: 600 }}>
                    Cliente
                  </div>
                  <div className="kv-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div className="kv"><span className="k">Nome</span><span className="v" style={{ fontWeight: 600 }}>{clientName}</span></div>
                    <div className="kv"><span className="k">Budget</span><span className="v mono">{fmtMoney(req.budget_complessivo)}</span></div>
                    <div className="kv"><span className="k">Liquidità</span><span className="v mono">{fmtMoney(req.liquidita)}</span></div>
                    <div className="kv"><span className="k">Tipologia</span><span className="v">{req.tipologia_acquirente ? (TIPOLOGIA_ACQ[req.tipologia_acquirente] ?? req.tipologia_acquirente) : '—'}</span></div>
                    <div className="kv no-line"><span className="k">Scopo</span><span className="v">{req.scopo_acquisto ?? '—'}</span></div>
                  </div>
                </div>
                {/* lotto */}
                <div style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                  <div style={{ fontSize: 'var(--fs-10)', color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--sp-2)', fontWeight: 600 }}>
                    Lotto · {propCode}
                  </div>
                  <div className="kv-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div className="kv"><span className="k">RGE</span><span className="v mono">{req.property_rge ?? '—'}</span></div>
                    <div className="kv"><span className="k">Data asta</span>
                      <span className="v" style={{ color: req.property_data_asta && daysTo(req.property_data_asta)! <= 10 ? 'var(--risk-ink)' : undefined }}>
                        {fmtDate(req.property_data_asta)}
                        {days != null && <DaysBadge days={days} />}
                      </span>
                    </div>
                    <div className="kv"><span className="k">Modalità</span><span className="v">{req.tipo_vendita ? (req.tipo_vendita === 'telematica' ? 'Telematica' : 'Fisica') : '—'}</span></div>
                    <div className="kv no-line"><span className="k">Sopralluogo</span>
                      <span className="v">
                        {req.sopralluogo_effettuato ? <span className="chip ok sm">Sì</span> : <span className="chip sm">No</span>}
                        {req.origin_appointment_id && (
                          <a href={`/sopralluoghi/${req.origin_appointment_id}`} style={{ marginLeft: 6, fontSize: 'var(--fs-11)', color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>S_*</a>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Dati Obbligatori Scheda */}
          <section id="dati-scheda" ref={sectionRefs['dati-scheda']} className="section">
            <div className="section-head"><div className="title">Dati Obbligatori Scheda</div></div>
            <div className="section-body">
              <div className="kv-grid">
                <div className="kv"><span className="k">Budget complessivo</span><span className="v mono" style={{ fontWeight: 600 }}>{fmtMoney(req.budget_complessivo)}</span></div>
                <div className="kv"><span className="k">Liquidità</span><span className="v mono">{fmtMoney(req.liquidita)}</span></div>
                <div className="kv"><span className="k">Mutuo necessario</span>
                  <span className="v">
                    {req.mutuo_necessario == null ? <span className="v muted">—</span> : req.mutuo_necessario ? <span className="chip info sm">Sì</span> : <span className="chip sm">No</span>}
                  </span>
                </div>
                <div className="kv"><span className="k">Tipologia acquirente</span><span className="v">{req.tipologia_acquirente ? (TIPOLOGIA_ACQ[req.tipologia_acquirente] ?? req.tipologia_acquirente) : '—'}</span></div>
                <div className="kv"><span className="k">Scopo acquisto</span><span className="v">{req.scopo_acquisto ?? '—'}</span></div>
                <div className="kv"><span className="k">Sopralluogo</span><span className="v">{req.sopralluogo_effettuato ? <span className="chip ok sm">Sì</span> : <span className="chip sm">No</span>}</span></div>
                <div className="kv no-line" style={{ gridColumn: '1/-1', flexDirection: 'column', gap: 'var(--sp-2)', display: 'flex' }}>
                  <span className="k" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    Lorenzo (broker) / Mutuabile
                  </span>
                  <LorenzoStatus req={req} />
                </div>
                {req.note !== undefined && (
                  <div className="kv no-line" style={{ gridColumn: '1/-1', flexDirection: 'column', gap: 'var(--sp-2)', display: 'flex', paddingBottom: 0 }}>
                    <span className="k" style={{ display: 'block' }}>Note interne</span>
                    <NoteEditor req={req} onUpdate={handleUpdate} onDirtyChange={setNoteDirty} />
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Redazione */}
          <section id="redazione" ref={sectionRefs.redazione} className="section">
            <div className="section-head">
              <div className="title">
                Redazione
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontWeight: 700, marginLeft: 4 }}>G</div>
                <span style={{ fontSize: 'var(--fs-12)', fontWeight: 400, color: 'var(--ink-3)', marginLeft: 4 }}>Giulia</span>
              </div>
            </div>
            <div className="section-body">
              <div className="kv-grid">
                <div className="kv">
                  <span className="k">Stato scheda</span>
                  <span className="v">{scdSt ? <span className={`chip ${scdSt.cls} sm`}>{scdSt.label}</span> : <span className="v empty">Non impostato</span>}</span>
                </div>
                <div className="kv">
                  <span className="k">Scadenza redazione</span>
                  <span className="v" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                    {fmtDate(req.scadenza_redazione)}
                    <DaysBadge days={daysTo(req.scadenza_redazione)} />
                  </span>
                </div>
                <div className="kv">
                  <span className="k">Presentata il</span>
                  <span className="v">{req.scd_presentata_at ? fmtDate(req.scd_presentata_at) : <span className="v empty">—</span>}</span>
                </div>
                <div className="kv no-line">
                  <span className="k">Flag operativi</span>
                  <div className="v">
                    <FlagRow label="Consulente sollecitato" on={!!req.consulente_sollecitato} onChange={v => void toggleFlag('consulente_sollecitato', v)} cls="on-warn" />
                    <FlagRow label="Contattare delegato" on={!!req.contattare_delegato} onChange={v => void toggleFlag('contattare_delegato', v)} cls="on-acc" />
                  </div>
                </div>
              </div>

              {/* Edit stato + scadenza */}
              <div style={{ marginTop: 'var(--sp-3)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)', paddingTop: 'var(--sp-3)', borderTop: '1px solid var(--line)' }}>
                <div>
                  <div style={{ fontSize: 'var(--fs-11)', color: 'var(--ink-3)', marginBottom: 'var(--sp-1)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cambia stato</div>
                  <ScdStatusEditor req={req} onUpdate={handleUpdate} onDirtyChange={setStatusDirty} />
                </div>
                <div>
                  <div style={{ fontSize: 'var(--fs-11)', color: 'var(--ink-3)', marginBottom: 'var(--sp-1)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Scadenza redazione</div>
                  <ScadenzaEditor req={req} onUpdate={handleUpdate} onDirtyChange={setScadenzaDirty} />
                </div>
              </div>

              <div style={{ marginTop: 'var(--sp-3)', paddingTop: 'var(--sp-3)', borderTop: '1px solid var(--line)' }}>
                <div style={{ fontSize: 'var(--fs-11)', color: 'var(--ink-3)', marginBottom: 'var(--sp-1)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Richiamare cliente entro</div>
                {req.scd_status === 'scheda_presentata' ? (
                  <ContattoClienteEditor req={req} onUpdate={handleUpdate} onDirtyChange={setContattoDirty} />
                ) : (
                  <span className="muted" style={{ fontSize: 'var(--fs-12)' }}>Disponibile una volta presentata la scheda.</span>
                )}
              </div>

            </div>
          </section>

          {/* Professionisti */}
          <section id="professionisti" ref={sectionRefs.professionisti} className="section">
            <div className="section-head">
              <div className="title">Professionisti</div>
            </div>
            <div className="section-body">
              <ProfessionalPicker
                tipo="delegato"
                value={req.delegato}
                onChange={async (prof) => {
                  const updated = await updateScdFields(req.id, {
                    delegato_id: prof?.id ?? null,
                    version: req.version,
                  })
                  handleUpdate(updated)
                }}
                extra={req.contattare_delegato ? <span className="chip accent sm">Da contattare</span> : undefined}
              />
            </div>
          </section>

          {/* Appuntamento */}
          <section id="appuntamento" ref={sectionRefs.appuntamento} className="section">
            <div className="section-head">
              <div className="title">Appuntamento Presentazione</div>
              {req.presentation_appointment_id && (
                <a href={`/sopralluoghi/${req.presentation_appointment_id}`}
                   style={{ fontSize: 'var(--fs-11)', padding: '2px 8px', borderRadius: 'var(--r-sm)', background: 'oklch(95% 0.025 145)', color: 'oklch(32% 0.12 145)', border: '1px solid oklch(83% 0.06 145)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  📅 Google Calendar
                </a>
              )}
            </div>
            <div className="section-body">
              <div className="kv-grid">
                <div className="kv">
                  <span className="k">Data e ora</span>
                  <span className="v mono" style={{ fontWeight: 600 }}>{fmtDateTime(req.data_presentazione)}</span>
                </div>
                <div className="kv">
                  <span className="k">Stato</span>
                  <span className="v">{req.scd_presentata_at ? <span className="chip ok sm">Presentata</span> : <span className="chip sm">Da fissare</span>}</span>
                </div>
                {presentationAppt?.location && (
                  <div className="kv">
                    <span className="k">Luogo</span>
                    <span className="v">{presentationAppt.location}</span>
                  </div>
                )}
                {presentationAppt?.notes && (
                  <div className="kv no-line">
                    <span className="k">Note</span>
                    <span className="v" style={{ whiteSpace: 'pre-line' }}>{presentationAppt.notes}</span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Documenti */}
          <section id="documenti" ref={sectionRefs.documenti} className="section">
            <div className="section-head"><div className="title">Checklist Documenti</div></div>
            <div className="section-body tight">
              <ScdChecklistTab scdRequest={req} onUpdate={handleUpdate} />
              <div style={{ padding: 'var(--sp-4)', borderTop: '1px solid var(--line)' }}>
                <div style={{ fontSize: 'var(--fs-11)', color: 'var(--ink-3)', marginBottom: 'var(--sp-1)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Scadenza checklist (Giulia)</div>
                <ChecklistScadenzaEditor req={req} onUpdate={handleUpdate} onDirtyChange={setChecklistDirty} />
              </div>
            </div>
          </section>

          {/* Domanda / Esito */}
          <section id="domanda-esito" ref={sectionRefs['domanda-esito']} className="section">
            <div className="section-head"><div className="title">Domanda Asta & Esito</div></div>
            <div className="section-body">
              <div className="kv-grid">
                <div className="kv">
                  <span className="k">Domanda fatta</span>
                  <span className="v">{req.domanda_fatta ? <span className="chip ok sm">Sì</span> : <span className="chip risk sm">No — da fare</span>}</span>
                </div>
                <div className="kv">
                  <span className="k">Scadenza offerta</span>
                  <span className="v" style={{ display: 'flex', alignItems: 'center' }}>
                    {fmtDate(req.scadenza_offerta)}
                    <DaysBadge days={daysTo(req.scadenza_offerta)} />
                  </span>
                </div>
                <div className="kv">
                  <span className="k">Partecipazione</span>
                  <span className="v">
                    {req.participation_id
                      ? <a href={`/partecipazioni/${req.participation_id}`} style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>P_*</a>
                      : <span className="v empty">— da creare</span>}
                  </span>
                </div>
                <div className="kv no-line">
                  <span className="k">Esito asta</span>
                  <span className="v">
                    {req.stage === 'aggiudicata' && <span className="chip ok">Aggiudicata</span>}
                    {req.stage === 'non_aggiudicata' && <span className="chip risk">Non aggiudicata</span>}
                    {!['aggiudicata','non_aggiudicata'].includes(req.stage) && <span className="v empty">—</span>}
                  </span>
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* === Right Rail === */}
        <div className="detail-rail">
          <ScdCommentThread scdId={id} comments={req.comments ?? []} />
          <section className="section">
            <div className="section-head"><div className="title">Attività Recente</div></div>
            <div className="section-body">
              <ScdEventTimeline scdId={id} />
            </div>
          </section>
        </div>
      </div>

      <ScdFissataModal
        open={fissataModalOpen}
        scdId={req.id}
        version={req.version}
        onClose={() => setFissataModalOpen(false)}
        onMoved={handleUpdate}
      />

      <UnsavedChangesModal
        open={blocker.status === 'blocked'}
        onStay={() => blocker.reset?.()}
        onLeave={() => blocker.proceed?.()}
      />
    </div>
  )
}

// ===== Inline editors (kept as sub-components) =====

function ScadenzaEditor({ req, onUpdate, onDirtyChange }: { req: ScdRequest; onUpdate: (u: ScdRequest) => void; onDirtyChange?: (dirty: boolean) => void }) {
  const toast = useToast()
  const { value: val, setValue: setVal, dirty, saving, doSave } = useSavableField(
    toLocalDatetimeInputValue(req.scadenza_redazione),
    (v) => setScdScadenza(req.id, { scadenza_redazione: new Date(v).toISOString(), version: req.version }),
    { onSaved: (u) => { onUpdate(u); toast.success('Scadenza salvata') }, onError: () => toast.error('Errore') },
  )
  const locked = req.scd_status === 'scheda_pronta' || req.scd_status === 'scheda_presentata'

  useEffect(() => { onDirtyChange?.(locked ? false : dirty) }, [dirty, locked, onDirtyChange])

  if (locked) {
    return (
      <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="datetime-local" value={toLocalDatetimeInputValue(req.scadenza_redazione)} disabled style={{ fontSize: 'var(--fs-13)', flex: 1 }} />
        <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>Non modificabile: scheda già pronta/presentata</span>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      <input type="datetime-local" value={val} onChange={e => setVal(e.target.value)} style={{ fontSize: 'var(--fs-13)', flex: 1 }} />
      <button type="button" className={`btn sm${dirty ? ' dirty' : ''}`} disabled={!val || saving} onClick={() => void doSave()}>
        {saving ? '…' : 'Salva'}
      </button>
      {dirty && <span className="dirty-badge">da salvare</span>}
    </div>
  )
}

function ContattoClienteEditor({ req, onUpdate, onDirtyChange }: { req: ScdRequest; onUpdate: (u: ScdRequest) => void; onDirtyChange?: (dirty: boolean) => void }) {
  const toast = useToast()
  const { value: val, setValue: setVal, dirty, saving, doSave } = useSavableField(
    toLocalDatetimeInputValue(req.scadenza_contatto_cliente),
    (v) => updateScdFields(req.id, { scadenza_contatto_cliente: v ? new Date(v).toISOString() : null, version: req.version }),
    { onSaved: (u) => { onUpdate(u); toast.success('Scadenza contatto salvata') }, onError: () => toast.error('Errore') },
  )
  useEffect(() => {
    onDirtyChange?.(dirty)
    return () => onDirtyChange?.(false)
  }, [dirty, onDirtyChange])
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      <input type="datetime-local" value={val} onChange={e => setVal(e.target.value)} style={{ fontSize: 'var(--fs-13)', flex: 1 }} />
      <button type="button" className={`btn sm${dirty ? ' dirty' : ''}`} disabled={saving} onClick={() => void doSave()}>
        {saving ? '…' : 'Salva'}
      </button>
      {dirty && <span className="dirty-badge">da salvare</span>}
    </div>
  )
}

function ChecklistScadenzaEditor({ req, onUpdate, onDirtyChange }: { req: ScdRequest; onUpdate: (u: ScdRequest) => void; onDirtyChange?: (dirty: boolean) => void }) {
  const toast = useToast()
  const { value: val, setValue: setVal, dirty, saving, doSave } = useSavableField(
    toLocalDatetimeInputValue(req.scadenza_checklist),
    (v) => updateScdFields(req.id, { scadenza_checklist: v ? new Date(v).toISOString() : null, version: req.version }),
    { onSaved: (u) => { onUpdate(u); toast.success('Scadenza checklist salvata') }, onError: () => toast.error('Errore') },
  )

  const editable = req.stage === 'esito_pos_checklist'

  useEffect(() => {
    onDirtyChange?.(editable ? dirty : false)
  }, [dirty, editable, onDirtyChange])

  if (!editable) {
    return (
      <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="datetime-local" value={toLocalDatetimeInputValue(req.scadenza_checklist)} disabled style={{ fontSize: 'var(--fs-13)', flex: 1 }} />
        <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>Modificabile solo in "Esito positivo - checklist"</span>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      <input type="datetime-local" value={val} onChange={e => setVal(e.target.value)} style={{ fontSize: 'var(--fs-13)', flex: 1 }} />
      <button type="button" className={`btn sm${dirty ? ' dirty' : ''}`} disabled={saving} onClick={() => void doSave()}>
        {saving ? '…' : 'Salva'}
      </button>
      {dirty && <span className="dirty-badge">da salvare</span>}
    </div>
  )
}

function ScdStatusEditor({ req, onUpdate, onDirtyChange }: { req: ScdRequest; onUpdate: (u: ScdRequest) => void; onDirtyChange?: (dirty: boolean) => void }) {
  const toast = useToast()
  const { value: val, setValue: setVal, dirty, saving, doSave } = useSavableField(
    req.scd_status ?? '',
    (v) => setScdStatus(req.id, { scd_status: v, version: req.version }),
    { onSaved: (u) => { onUpdate(u); toast.success('Stato SCD salvato') }, onError: () => toast.error('Errore') },
  )
  useEffect(() => { onDirtyChange?.(dirty) }, [dirty, onDirtyChange])
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={val} onChange={e => setVal(e.target.value)} style={{ fontSize: 'var(--fs-13)', flex: 1 }}>
        <option value="">— Nessuno —</option>
        <option value="scheda_da_fare">Scheda da fare</option>
        <option value="scheda_pronta">Scheda pronta</option>
        <option value="scd_da_fix">SCD da fix</option>
        <option value="scheda_presentata">Scheda presentata</option>
      </select>
      <button type="button" className={`btn sm${dirty ? ' dirty' : ''}`} disabled={saving} onClick={() => void doSave()}>
        {saving ? '…' : 'Salva'}
      </button>
      {dirty && <span className="dirty-badge">da salvare</span>}
    </div>
  )
}
